import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarCheck, Mail, Heart, Check, ChevronDown, AlertTriangle, Star, Facebook, Instagram, Phone, MessageSquareText, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { getRef } from "../../lib/ref";
import { Logo } from "../Logo";

export const BookingBand = ({ landing, property }) => (
  <section id="availability" className="scroll-mt-24 bg-sand py-16 sm:py-24" aria-label="Availability and booking" data-testid="booking-band">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
      <div className="text-center mb-10">
        <p className="text-xs uppercase tracking-[0.24em] text-copper font-semibold mb-2">Availability & Booking</p>
        <h2 className="font-serif text-3xl sm:text-5xl font-semibold text-charcoal">Your farm escape <em className="text-copperdeep">awaits</em></h2>
        <p className="text-soft text-lg mt-3 max-w-2xl mx-auto">Every stay is arranged personally with the Elite STR Hosting Co. team — call or text us with your dates.</p>
      </div>
      {landing.ownerrez_widget_url ? (
        <div className="bg-white rounded-3xl border border-charcoal/10 shadow-lg overflow-hidden" data-testid="ownerrez-widget">
          <iframe src={landing.ownerrez_widget_url} title="Live availability calendar" className="w-full h-[640px] border-0" loading="lazy" />
        </div>
      ) : (
        <div className="bg-charcoal text-cream rounded-3xl shadow-xl px-6 sm:px-12 py-12 text-center" data-testid="booking-card">
          <CalendarCheck size={34} className="mx-auto text-[#E8A97E] mb-4" aria-hidden />
          <h3 className="font-serif text-2xl sm:text-3xl font-semibold">Plan your stay</h3>
          <p className="text-cream/75 text-lg mt-2 max-w-xl mx-auto">Every stay is arranged personally with the host — tell us your dates and we'll take care of the rest.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-7">
            <a href="#contact"
              className="inline-flex items-center gap-2 bg-copper text-cream font-semibold rounded-full px-8 py-4 text-lg hover:bg-copperdeep transition-colors"
              data-testid="booking-inquiry-button">
              <Mail size={17} aria-hidden /> Send an Inquiry
            </a>
            <a href={`tel:${property.phone_tel}`}
              className="inline-flex items-center gap-2 border-2 border-cream/40 text-cream font-semibold rounded-full px-8 py-4 text-lg hover:bg-cream/10 transition-colors"
              data-testid="booking-call-button">
              <Phone size={17} aria-hidden /> Call or text {property.phone_display}
            </a>
          </div>
        </div>
      )}
    </div>
  </section>
);

const WEDDING_PHOTOS = [
  { url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_welcome-guide-str/artifacts/5bf5bdc0891ab4d5_Sunlit%20Garden%20Wedding%20Ceremony.png", alt: "Garden ceremony arch with florals on the Pebble Farms grounds", cls: "row-span-2 aspect-[3/4.1]" },
  { url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_welcome-guide-str/artifacts/e660ba3ce9545656_Joyful%20Wedding%20Dance%20on%20the%20Driveway.png", alt: "Wedding party celebrating on the drive", cls: "aspect-square" },
  { url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_welcome-guide-str/artifacts/f7aa96b24b1549fc_Bridal%20Preparation%20in%20an%20Elegant%20Bedroom.png", alt: "Bridal party getting ready in the primary suite", cls: "aspect-square" },
  { url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_welcome-guide-str/artifacts/2c52dfd99a2b561f_Elegant%20Bridal%20Bathroom%20Preparations.png", alt: "Hair and makeup in the primary bathroom", cls: "aspect-square" },
  { url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_welcome-guide-str/artifacts/48313e6d7a81c88a_Elegant%20Garden%20Wedding%20Bar.png", alt: "Outdoor bar setup among the crepe myrtles", cls: "aspect-square" },
  { url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_welcome-guide-str/artifacts/316b499c848b870d_Groomsmen%20Strolling%20Through%20Blooming%20Gardens.png", alt: "Groom and groomsmen strolling the blooming grounds", cls: "col-span-2 aspect-[16/9]" },
];

export const Weddings = ({ weddings, property }) => (
  <section id="weddings" className="scroll-mt-24 py-16 sm:py-24 relative overflow-hidden" aria-label="Weddings and gatherings" data-testid="weddings-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid lg:grid-cols-2 gap-12 items-center">
      <motion.div initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7 }}>
        <p className="text-xs uppercase tracking-[0.24em] text-copper font-semibold mb-2">Weddings & Gatherings</p>
        <h2 className="font-serif text-3xl sm:text-5xl font-semibold text-charcoal leading-tight">{weddings.headline}</h2>
        <p className="text-soft text-lg mt-4">{weddings.sub}</p>
        <ul className="space-y-3.5 mt-7">
          {weddings.points.map((point, i) => (
            <motion.li key={i} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.45 }}
              className="flex gap-3 text-charcoal/90 text-base leading-relaxed">
              <span className="mt-1 p-1 rounded-full bg-sage/20 shrink-0"><Check size={14} className="text-sagedeep" aria-hidden /></span>
              {point}
            </motion.li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-3 mt-8">
          <a href={`tel:${property.phone_tel}`}
            className="inline-flex items-center gap-2 bg-copper text-cream font-semibold rounded-full px-7 py-3.5 text-lg hover:bg-copperdeep transition-colors shadow-md"
            data-testid="wedding-call-to-book-button">
            <Phone size={17} aria-hidden /> {weddings.cta} · {property.phone_display}
          </a>
          <a href="#contact" className="inline-flex items-center border-2 border-charcoal/20 text-charcoal font-semibold rounded-full px-7 py-3.5 hover:border-copper transition-colors" data-testid="wedding-inquiry-button">
            Send an event inquiry
          </a>
        </div>
      </motion.div>
      <div className="relative">
        <div className="grid grid-cols-2 gap-3 sm:gap-4" data-testid="wedding-photo-collage">
          {WEDDING_PHOTOS.map((photo, i) => (
            <motion.div
              key={photo.url}
              initial={{ opacity: 0, y: 26, scale: 0.97 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: i * 0.1, duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
              className={`overflow-hidden rounded-2xl shadow-lg group ${photo.cls} ${i % 2 ? "rotate-1" : "-rotate-1"}`}
            >
              <img src={photo.url} alt={photo.alt} loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                data-testid={`wedding-photo-${i}`} />
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.5, duration: 0.6 }}
          className="absolute -bottom-5 -left-3 sm:-left-5 bg-white rounded-2xl shadow-xl px-5 py-4 -rotate-2"
        >
          <p className="font-serif italic text-copperdeep text-lg flex items-center gap-2"><Heart size={16} aria-hidden /> Approved events welcome</p>
          <p className="text-xs text-soft mt-0.5">Advance host approval · separate agreement</p>
        </motion.div>
      </div>
    </div>
  </section>
);

export const Rules = ({ rules }) => (
  <section className="bg-creamdark py-16 sm:py-24" aria-label="House rules and host" data-testid="rules-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
        <div>
          <p className="text-xs uppercase tracking-[0.24em] text-copper font-semibold mb-2">Good to Know</p>
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-charcoal mb-7">House rules & policies</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {rules.items.map((rule, i) => (
              <motion.div key={rule} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05, duration: 0.4 }}
                className="bg-white border border-charcoal/10 rounded-xl px-4 py-3.5 flex items-center gap-3"
                data-testid={`rule-${i}`}>
                <Check size={16} className="text-sagedeep shrink-0" aria-hidden />
                <span className="text-charcoal font-medium text-[15px]">{rule}</span>
              </motion.div>
            ))}
          </div>
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="mt-6 bg-[#FBF1EA] border-2 border-copper/40 rounded-2xl p-5 sm:p-6" data-testid="child-safety-notice">
            <h3 className="flex items-center gap-2.5 font-serif font-semibold text-lg text-copperdeep mb-2">
              <AlertTriangle size={19} aria-hidden /> Child Safety Notice
            </h3>
            <p className="text-soft leading-relaxed">{rules.child_safety}</p>
          </motion.div>
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.24em] text-copper font-semibold mb-2">Meet Your Host</p>
          <div className="bg-white border border-charcoal/10 rounded-3xl p-7 sm:p-9 shadow-sm" data-testid="host-card">
            <div className="flex items-center gap-4 mb-5">
              <span className="w-14 h-14 rounded-full bg-charcoal text-cream flex items-center justify-center font-serif text-xl font-semibold" aria-hidden>ES</span>
              <div>
                <h3 className="font-serif text-2xl font-semibold text-charcoal">{rules.host.name}</h3>
                <p className="text-copperdeep text-sm font-semibold flex items-center gap-1.5"><Star size={13} aria-hidden /> {rules.host.tagline}</p>
              </div>
            </div>
            <p className="text-soft leading-relaxed">{rules.host.bio}</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-6">
              {rules.host.perks.map((perk) => (
                <li key={perk} className="flex items-center gap-2.5 text-charcoal/90 text-[15px]">
                  <Check size={15} className="text-sagedeep shrink-0" aria-hidden /> {perk}
                </li>
              ))}
            </ul>
            <p className="text-sm text-soft mt-6 pt-5 border-t border-charcoal/10">{rules.host.response}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const Faq = ({ faqs, heading = "Good to know", sub = "Still curious? Call or text us — we reply fast." }) => {
  const [open, setOpen] = useState(null);
  return (
    <section id="faq" className="scroll-mt-24 py-16 sm:py-24" aria-label="Frequently asked questions" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-8">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.24em] text-copper font-semibold mb-2">FAQ</p>
          <h2 className="font-serif text-3xl sm:text-5xl font-semibold text-charcoal">{heading}</h2>
          <p className="text-soft text-lg mt-3">{sub}</p>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className={`bg-white border rounded-2xl transition-colors ${open === i ? "border-copper/40 shadow-md" : "border-charcoal/10"}`} data-testid={`faq-item-${i}`}>
              <h3>
                <button onClick={() => setOpen(open === i ? null : i)} aria-expanded={open === i} aria-controls={`faq-${i}-body`}
                  className="w-full flex items-center justify-between gap-4 text-left px-5 sm:px-6 py-4 sm:py-5 min-h-[56px]"
                  data-testid={`faq-toggle-${i}`}>
                  <span className="font-serif text-lg font-semibold text-charcoal">{faq.q}</span>
                  <ChevronDown size={19} aria-hidden className={`shrink-0 text-soft transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
                </button>
              </h3>
              <div id={`faq-${i}-body`} role="region" className={`panel-body ${open === i ? "open" : ""}`}>
                <div><p className="px-5 sm:px-6 pb-5 text-soft leading-relaxed">{faq.a}</p></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PURPOSES = ["Family getaway", "Friends trip", "Wedding / event", "Corporate retreat", "Other"];

export const Contact = ({ property }) => {
  const [form, setForm] = useState({ name: "", email: "", checkin: "", checkout: "", group_size: "", purpose: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/inquiries", { ...form, ref: getRef() });
      toast.success("Inquiry sent — the Elite STR team will reply shortly.");
      setForm({ name: "", email: "", checkin: "", checkout: "", group_size: "", purpose: "", message: "" });
    } catch {
      toast.error("Couldn't send — please call or text (210) 274-3556.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-white border border-charcoal/15 rounded-xl px-4 py-3.5 text-base outline-none focus:border-copper transition-colors";

  return (
    <section id="contact" className="scroll-mt-24 bg-charcoal text-cream py-16 sm:py-24" aria-label="Contact" data-testid="contact-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-8">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.24em] text-[#E8A97E] font-semibold mb-2">Get in Touch</p>
          <h2 className="font-serif text-3xl sm:text-5xl font-semibold">Plan your <em className="text-[#EBC9A8]">gathering</em></h2>
          <p className="text-cream/75 text-lg mt-3">Share a few details about your trip and we'll help you plan the perfect Canyon Lake stay.</p>
        </div>
        <form onSubmit={submit} className="bg-cream text-charcoal rounded-3xl p-6 sm:p-9 shadow-2xl space-y-4" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <input required value={form.name} onChange={set("name")} placeholder="Name" aria-label="Name" className={inputCls} data-testid="contact-name-input" />
            <input required type="email" value={form.email} onChange={set("email")} placeholder="Email" aria-label="Email" className={inputCls} data-testid="contact-email-input" />
            <label className="block">
              <span className="text-xs font-semibold text-soft">Check-in</span>
              <input type="date" value={form.checkin} onChange={set("checkin")} aria-label="Check-in date" className={inputCls + " mt-1"} data-testid="contact-checkin-input" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-soft">Check-out</span>
              <input type="date" value={form.checkout} onChange={set("checkout")} aria-label="Check-out date" className={inputCls + " mt-1"} data-testid="contact-checkout-input" />
            </label>
            <input value={form.group_size} onChange={set("group_size")} placeholder="Group size" aria-label="Group size" className={inputCls} data-testid="contact-group-input" />
            <select value={form.purpose} onChange={set("purpose")} aria-label="Purpose of stay" className={inputCls} data-testid="contact-purpose-select">
              <option value="">Purpose of stay</option>
              {PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <textarea value={form.message} onChange={set("message")} rows={4} placeholder="Tell us about your trip — dates, occasion, anything special…" aria-label="Message" className={inputCls} data-testid="contact-message-input" />
          <button type="submit" disabled={busy}
            className="w-full bg-copper text-cream font-semibold rounded-full py-4 text-lg hover:bg-copperdeep transition-colors disabled:opacity-50"
            data-testid="contact-submit-button">
            {busy ? "Sending…" : "Send Inquiry"}
          </button>
          <p className="text-center text-sm text-soft">
            Prefer to talk? <a href={`tel:${property.phone_tel}`} className="font-semibold text-copperdeep underline underline-offset-2" data-testid="contact-call-link">Call or text {property.phone_display}</a>
          </p>
        </form>
      </div>
    </section>
  );
};

export const Footer = ({ property }) => (
  <footer className="bg-creamdark border-t border-charcoal/10" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-14">
      <div className="flex flex-col md:flex-row justify-between gap-10">
        <div className="max-w-xs">
          <Logo />
          <p className="text-soft mt-4 leading-relaxed">{property.address}, {property.city_state_zip}<br />Hosted by {property.host}</p>
          <div className="flex gap-2.5 mt-5">
            <a href={`tel:${property.phone_tel}`} className="p-3 bg-white border border-charcoal/10 rounded-full hover:border-copper transition-colors" aria-label="Call the host" data-testid="footer-call"><Phone size={17} className="text-copper" /></a>
            <a href={`sms:${property.phone_sms}`} className="p-3 bg-white border border-charcoal/10 rounded-full hover:border-copper transition-colors" aria-label="Text the host" data-testid="footer-text"><MessageSquareText size={17} className="text-copper" /></a>
          </div>
        </div>
        <nav className="grid grid-cols-2 gap-x-14 gap-y-3 content-start" aria-label="Footer">
          <Link to="/login" className="font-semibold text-charcoal hover:text-copperdeep transition-colors inline-flex items-center gap-2" data-testid="footer-guide-link"><BookOpen size={15} aria-hidden /> Guest Login</Link>
          <a href={property.brand_url} target="_blank" rel="noreferrer" className="font-semibold text-charcoal hover:text-copperdeep transition-colors" data-testid="footer-brand-link">{property.host}</a>
        </nav>
        <div className="flex flex-col gap-2.5 content-start">
          <a href={property.facebook} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 bg-white border border-charcoal/10 rounded-full px-5 py-3 font-semibold text-charcoal hover:border-copper hover:text-copperdeep transition-colors" data-testid="footer-facebook">
            <Facebook size={17} aria-hidden /> Follow us on Facebook
          </a>
          <a href={property.instagram} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 bg-white border border-charcoal/10 rounded-full px-5 py-3 font-semibold text-charcoal hover:border-copper hover:text-copperdeep transition-colors" data-testid="footer-instagram">
            <Instagram size={17} aria-hidden /> Follow us on Instagram
          </a>
        </div>
      </div>
      <p className="text-xs text-soft mt-12">© {new Date().getFullYear()} {property.host} · Pebble Farms, Canyon Lake, Texas</p>
    </div>
  </footer>
);
